// FILE: src/components/app/UploadDataComponent/DataTable/TableRow/TableRow.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faTrash, faPen, faSave, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./TableRow.module.css";
import { splitTagPathsCell } from "../utils/tagUtils";
import { TransactionsAPI, SystemAPI } from "../../../../../utils/firebase";

/* -----------------------------------------------------------------------------
   Module-level caches to prevent N fetches for N rows (and StrictMode double mount)
----------------------------------------------------------------------------- */
let __txnTypesCache = null;      // normalized array
let __txnTypesPending = null;    // Promise while fetching
let __currenciesCache = null;    // normalized array
let __currenciesPending = null;  // Promise while fetching

function fallbackTxnTypes() {
  return [
    { id: "expense",  name: "Expense",  symbol: "-" },
    { id: "income",   name: "Income",   symbol: "+" },
    { id: "transfer", name: "Transfer", symbol: "+" },
  ];
}

function normalizeCurrencies(items) {
  const out = [];
  const seen = new Set();
  (items || []).forEach((raw) => {
    const code = String(raw?.currencyCode ?? raw?.code ?? raw?.iso ?? "")
      .trim()
      .toUpperCase();
    if (!code || seen.has(code)) return;
    seen.add(code);
    out.push({
      currencyCode: code,
      currencyName: String(raw?.currencyName || raw?.name || "").trim(),
      symbol: String(raw?.symbol || raw?.sym || "").trim(),
    });
  });
  return out;
}

function normalizeTxnTypes(items) {
  return (items || [])
    .map((x) => {
      const name = x?.name ? String(x.name) : "";
      const rawSym =
        x?.symbol ??
        x?.sign ??
        (typeof x?.operator === "string"
          ? x.operator.toLowerCase().includes("debit") ? "-" : "+"
          : undefined) ??
        (name.match(/^exp/i) ? "-" : "+");
      return {
        name,
        symbol: rawSym === "-" ? "-" : "+",
        id: x?.id ? String(x.id) : undefined,
      };
    })
    .filter((x) => x.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

async function getTxnTypesOnce() {
  if (__txnTypesCache) return __txnTypesCache;
  if (__txnTypesPending) return __txnTypesPending;
  __txnTypesPending = (async () => {
    try {
      const list = await TransactionsAPI.fetchTransactionTypes();
      const norm = normalizeTxnTypes(list);
      __txnTypesCache = norm.length ? norm : fallbackTxnTypes();
      return __txnTypesCache;
    } catch {
      __txnTypesCache = fallbackTxnTypes();
      return __txnTypesCache;
    } finally {
      __txnTypesPending = null;
    }
  })();
  return __txnTypesPending;
}

async function getCurrenciesOnce() {
  if (__currenciesCache) return __currenciesCache;
  if (__currenciesPending) return __currenciesPending;
  __currenciesPending = (async () => {
    try {
      const list = await SystemAPI.fetchCurrencyList();
      __currenciesCache = normalizeCurrencies(list);
      return __currenciesCache;
    } catch {
      __currenciesCache = [];
      return __currenciesCache;
    } finally {
      __currenciesPending = null;
    }
  })();
  return __currenciesPending;
}

/* -----------------------------------------------------------------------------
   Component
----------------------------------------------------------------------------- */
export default function TableRow({
  row,
  headers,
  hiddenColumns = [],
  onDelete,
  onRemoveTagPath, // (rowIndex, pathString) -> void
  rowIndex,
  onSaveRow, // (rowIndex, updatedRowArray) -> void
  currencies: currenciesProp, // ⚠️ no [] default here (prevents identity churn)
  tagOptions = [],
  accountOptions = [],
}) {
  // Map header names -> indices once
  const idx = useMemo(() => {
    const map = new Map();
    headers?.forEach((h, i) => map.set(h, i));
    return {
      Tags: map.get("Tags"),
      Date: map.get("Date"),
      Description: map.get("Description"),
      Currency: map.get("Currency"),
      Type: map.get("Type"),
      Account: map.get("Account"),
      Amount: map.get("Amount"),
    };
  }, [headers]);

  // Always hide the Status column for this view
  const effectiveHidden = useMemo(() => {
    const set = new Set(["Status", ...hiddenColumns]);
    return Array.from(set);
  }, [hiddenColumns]);

  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState(() => rowToValues(row, headers));

  const [currencies, setCurrencies] = useState([]);
  const [txnTypes, setTxnTypes] = useState([]);
  const [loading, setLoading] = useState({ currencies: false, types: false });
  const [errors, setErrors] = useState({ currencies: "", types: "" });

  // Optimistic display row (array) used after Save until parent props update
  const [optimisticRow, setOptimisticRow] = useState(null);

  // ---------- helpers ----------
  const deepEqual = (a, b) => {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  };

  function rowToValues(r, hdrs) {
    const obj = {};
    (hdrs || []).forEach((h, i) => (obj[h] = r?.[i]));
    if (obj.Date) obj.Date = toISODate(obj.Date);
    if (obj.Tags && Array.isArray(obj.Tags)) obj.Tags = obj.Tags.join(", ");
    return obj;
  }

  function toISODate(v) {
    if (!v) return "";
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function valuesToRowArray(values, hdrs) {
    return (hdrs || []).map((h) => {
      if (h === "Tags") {
        return (values[h] || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .join("; ");
      }
      return values[h];
    });
  }

  // ---------- state syncing & fetching (loop-safe) ----------

  // Use the optimistic row (if present) as the source of truth for base values
  const sourceRow = optimisticRow || row;

  // Derive once per real change in sourceRow/headers
  const baseValues = useMemo(() => rowToValues(sourceRow, headers), [sourceRow, headers]);

  // Keep local state in sync when *not* editing and values truly changed
  useEffect(() => {
    if (!isEditing && !deepEqual(editValues, baseValues)) {
      setEditValues(baseValues);
    }
  }, [baseValues, isEditing, editValues]);

  // When parent row updates to match our optimisticRow, clear the optimistic override
  useEffect(() => {
    if (optimisticRow && deepEqual(optimisticRow, row)) {
      setOptimisticRow(null);
    }
  }, [row, optimisticRow]);

  // If parent supplies currencies later, adopt them (no identity-based dependency)
  useEffect(() => {
    if (!Array.isArray(currenciesProp) || currenciesProp.length === 0) return;
    const normalized = normalizeCurrencies(currenciesProp);
    if (!deepEqual(normalized, currencies)) {
      setCurrencies(normalized);
    }
  }, [currenciesProp, currencies]);

  // Fetch lists once (shared across rows via module caches)
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading((s) => ({ ...s, currencies: true, types: true }));
      setErrors({ currencies: "", types: "" });
      try {
        const [cur, types] = await Promise.all([getCurrenciesOnce(), getTxnTypesOnce()]);
        if (!ignore) {
          if (!deepEqual(cur, currencies)) setCurrencies(cur);
          if (!deepEqual(types, txnTypes)) setTxnTypes(types);
        }
      } catch {
        if (!ignore) {
          if (currencies.length === 0) setErrors((er) => ({ ...er, currencies: "Failed to load currencies" }));
          if (txnTypes.length === 0) setErrors((er) => ({ ...er, types: "Failed to load types" }));
        }
      } finally {
        if (!ignore) setLoading((s) => ({ ...s, currencies: false, types: false }));
      }
    })();
    return () => { ignore = true; };
  }, []); // ✅ run once; results cached across rows

  // ---------- handlers ----------
  const handleEditClick = () => {
    setEditValues(baseValues);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValues(baseValues);
    setIsEditing(false);
  };

  const handleSave = () => {
    const updated = valuesToRowArray(editValues, headers);
    setOptimisticRow(updated);
    onSaveRow?.(rowIndex, updated);
    setIsEditing(false);
  };

  const handleChange = (field, value) => {
    setEditValues((prev) => {
      if (field === "Type") {
        const selected = txnTypes.find((t) => t.name === value);
        const sym = selected?.symbol === "-" ? "-" : "+";
        const next = { ...prev, Type: value };
        const asNum = Number(next.Amount);
        if (!Number.isNaN(asNum)) {
          const magnitude = Math.abs(asNum);
          next.Amount = sym === "-" ? -magnitude : magnitude;
        }
        return next;
      }
      return { ...prev, [field]: value };
    });
  };

  // Optimistically remove a tag in-display, then notify parent
  const handleRemoveTag = (pathToRemove) => {
    const tagCol = idx.Tags;
    if (typeof tagCol !== "number") {
      onRemoveTagPath?.(rowIndex, pathToRemove);
      return;
    }
    const current = optimisticRow || row || [];
    const currentCell = current[tagCol] ?? "";
    const parts = splitTagPathsCell(currentCell);
    const nextParts = parts.filter((p) => p !== pathToRemove);
    const nextCell = nextParts.join("; ");
    const nextRow = [...current];
    nextRow[tagCol] = nextCell;
    setOptimisticRow(nextRow);
    onRemoveTagPath?.(rowIndex, pathToRemove);
  };

  // ---------- Tags typeahead (edit mode) ----------
  const tagsInputRef = useRef(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [tagsHighlight, setTagsHighlight] = useState(0);

  const tagTokens = useMemo(() => {
    return String(editValues.Tags || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [editValues.Tags]);

  const lastFragment = useMemo(() => {
    const raw = String(editValues.Tags || "");
    const commaIdx = raw.lastIndexOf(",");
    return (commaIdx === -1 ? raw : raw.slice(commaIdx + 1)).trimStart();
  }, [editValues.Tags]);

  const tagSuggestions = useMemo(() => {
    const existing = new Set(tagTokens.map((t) => t.toLowerCase()));
    const base = (tagOptions || []).filter(Boolean);
    const pool = base.filter((t) => !existing.has(String(t).toLowerCase()));
    if (!lastFragment) return pool.slice(0, 20);
    const lf = lastFragment.toLowerCase();
    return pool.filter((t) => String(t).toLowerCase().startsWith(lf)).slice(0, 20);
  }, [tagOptions, tagTokens, lastFragment]);

  const replaceLastFragment = (full, replacement) => {
    const raw = String(full || "");
    const idx = raw.lastIndexOf(",");
    const before = idx === -1 ? "" : raw.slice(0, idx + 1) + " ";
    return (before + replacement).replace(/\s+$/g, "");
  };

  const acceptTagSuggestion = (value) => {
    const replaced = replaceLastFragment(editValues.Tags || "", value);
    const withComma = replaced.length ? `${replaced}, ` : `${value}, `;
    handleChange("Tags", withComma);
    setTagsOpen(true);
    setTagsHighlight(0);
    requestAnimationFrame(() => tagsInputRef.current?.focus());
  };

  const onTagsKeyDown = (e) => {
    if (!tagsOpen && ["ArrowDown", "Enter", "Tab"].includes(e.key)) {
      setTagsOpen(true);
    }
    if (!tagsOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setTagsHighlight((i) => Math.min(i + 1, Math.max(tagSuggestions.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setTagsHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (tagSuggestions.length > 0) {
        e.preventDefault();
        acceptTagSuggestion(tagSuggestions[tagsHighlight] || tagSuggestions[0]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setTagsOpen(false);
    } else if (e.key === ",") {
      setTagsOpen(true);
      setTagsHighlight(0);
    }
  };

  // ---------- render helpers ----------
  const renderTagsDisplayCell = (cell, i) => {
    const paths = splitTagPathsCell(cell);
    return (
      <td key={i} className={styles.tableCell}>
        <div className={styles.tagWrap}>
          {paths.length === 0 ? (
            <span className="text-secondary small">—</span>
          ) : (
            paths.map((p, idx2) => (
              <span key={`${p}-${idx2}`} className={` rounded-pill ${styles.tag}`}>
                <span className={styles.tagText}>{p}</span>
                <button
                  type="button"
                  className={styles.tagRemove}
                  aria-label={`Remove ${p}`}
                  title={`Remove ${p}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTag(p);
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </span>
            ))
          )}
        </div>
      </td>
    );
  };

  const applyAmountSign = (raw, symbol) => {
    if (raw === "" || raw === null || raw === undefined) return raw;
    const n = Number(raw);
    if (Number.isNaN(n)) return raw;
    const abs = Math.abs(n);
    return symbol === "-" ? -abs : abs;
  };

  const renderEditableCell = (header, i) => {
    if (i === idx.Date || header === "Date") {
      return (
        <td key={i} className={`${styles.tableCell} ${styles.dateCell}`}>
          <input
            type="date"
            className={`form-control form-control-sm ${styles.dateInput}`}
            value={editValues.Date || ""}
            onChange={(e) => handleChange("Date", e.target.value)}
          />
        </td>
      );
    }
    if (i === idx.Description || header === "Description") {
      return (
        <td key={i} className={`${styles.tableCell} ${styles.descriptionCell}`}>
          <input
            type="text"
            className="form-control form-control-sm"
            value={editValues.Description || ""}
            onChange={(e) => handleChange("Description", e.target.value)}
          />
        </td>
      );
    }
    if (i === idx.Amount || header === "Amount") {
      const selectedType = txnTypes.find((t) => t.name === editValues.Type);
      const typeSymbol = selectedType?.symbol === "-" ? "-" : "+";
      const amountNum = Number(editValues.Amount);
      const magnitude =
        editValues.Amount === "" || Number.isNaN(amountNum) ? (editValues.Amount ?? "") : Math.abs(amountNum);

      return (
        <td key={i} className={`${styles.tableCell} ${styles.amountCell}`}>
          <div className={`input-group input-group-sm ${styles.amountGroup}`}>
            <span className={`input-group-text ${styles.amountPrefix}`} aria-label="Amount sign">
              {typeSymbol}
            </span>
            <input
              type="number"
              className={`form-control ${styles.amountInput}`}
              value={magnitude}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleChange("Amount", "");
                  return;
                }
                const n = Number(val);
                if (Number.isNaN(n)) {
                  handleChange("Amount", val);
                } else {
                  handleChange("Amount", applyAmountSign(n, typeSymbol));
                }
              }}
            />
          </div>
        </td>
      );
    }
    if (i === idx.Currency || header === "Currency") {
      const selectedCode = String(editValues.Currency || "").toUpperCase();
      const selected = currencies.find((x) => x.currencyCode === selectedCode);
      const title = selected ? `${selected.currencyName}${selected.symbol ? ` - ${selected.symbol}` : ""}` : "";
      return (
        <td key={i} className={styles.tableCell}>
          <select
            className="form-select form-select-sm"
            value={selectedCode}
            onChange={(e) => handleChange("Currency", e.target.value.toUpperCase())}
            disabled={loading.currencies}
            title={title}
          >
            <option value="">{loading.currencies ? "Loading…" : "Select…"}</option>
            {currencies.map((c) => (
              <option key={`cc-${c.currencyCode}`} value={c.currencyCode}>
                {c.currencyCode}
              </option>
            ))}
          </select>
          {errors.currencies && <div className="form-text text-danger">{errors.currencies}</div>}
        </td>
      );
    }
    if (i === idx.Type || header === "Type") {
      return (
        <td key={i} className={styles.tableCell}>
          <select
            className="form-select form-select-sm"
            value={editValues.Type || ""}
            onChange={(e) => handleChange("Type", e.target.value)}
            disabled={loading.types}
          >
            <option value="">{loading.types ? "Loading…" : "Select…"}</option>
            {txnTypes.map((t) => (
              <option key={`tx-${t.id || t.name}`} value={t.name}>
                {t.name} {t.symbol ? `(${t.symbol})` : ""}
              </option>
            ))}
          </select>
          {errors.types && <div className="form-text text-danger">{errors.types}</div>}
        </td>
      );
    }
    if (i === idx.Tags || header === "Tags") {
      const menuId = `tags-menu-${rowIndex}`;
      return (
        <td key={i} className={`${styles.tableCell} ${styles.tagsCell}`}>
          <div className={styles.tagsTypeaheadWrap}>
            <input
              ref={tagsInputRef}
              type="text"
              className="form-control form-control-sm"
              placeholder="Comma-separated tags"
              value={editValues.Tags || ""}
              onFocus={() => setTagsOpen(true)}
              onChange={(e) => {
                handleChange("Tags", e.target.value);
                setTagsOpen(true);
                setTagsHighlight(0);
              }}
              onKeyDown={onTagsKeyDown}
            />
            {tagsOpen && (
              <div id={menuId} role="listbox" className={styles.tagMenu}>
                {tagSuggestions.length === 0 ? (
                  <div className={`${styles.tagMenuItem} ${styles.tagMenuEmpty}`}>No suggestions</div>
                ) : (
                  tagSuggestions.map((s, k) => (
                    <button
                      type="button"
                      key={`sugg-${s}-${k}`}
                      className={`${styles.tagMenuItem} ${k === tagsHighlight ? styles.active : ""}`}
                      onMouseEnter={() => setTagsHighlight(k)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        acceptTagSuggestion(s);
                      }}
                    >
                      {s}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </td>
      );
    }
    if (i === idx.Account || header === "Account") {
      const listId = `acct-list-${rowIndex}`;
      return (
        <td key={i} className={styles.tableCell}>
          <input
            type="text"
            className="form-control form-control-sm"
            list={listId}
            value={editValues.Account || ""}
            onChange={(e) => handleChange("Account", e.target.value)}
          />
          <datalist id={listId}>
            {accountOptions.filter(Boolean).map((a, k) => (
              <option key={`acct-${a}-${k}`} value={a} />
            ))}
          </datalist>
        </td>
      );
    }

    return (
      <td key={i} className={styles.tableCell}>
        <input
          type="text"
          className="form-control form-control-sm"
          value={editValues[header] ?? ""}
          onChange={(e) => handleChange(header, e.target.value)}
        />
      </td>
    );
  };

  // ---------- render ----------
  const displayRow = optimisticRow || row;

  return (
    <tr>
      {displayRow.map((cell, i) => {
        const headerName = headers[i];
        if (effectiveHidden.includes(headerName)) return null;

        if (isEditing) return renderEditableCell(headerName, i);

        if (i === idx.Tags) return renderTagsDisplayCell(cell, i);

        const extraClass =
          i === idx.Date
            ? styles.dateCell
            : i === idx.Description
            ? styles.descriptionCell
            : "";

        return (
          <td key={i} className={`${styles.tableCell} ${extraClass}`}>
            {cell ?? <span className="text-secondary small">—</span>}
          </td>
        );
      })}

      <td className={`text-center ${styles.actions}`}>
        {!isEditing ? (
          <div className="d-inline-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleEditClick}
              aria-label="Edit row"
              title="Edit row"
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={onDelete}
              aria-label="Delete row"
              title="Delete row"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        ) : (
          <div className="d-inline-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={handleSave}
              aria-label="Save row"
              title="Save row"
            >
              <FontAwesomeIcon icon={faSave} />
              <span className="visually-hidden"> Save</span>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleCancel}
              aria-label="Cancel edit"
              title="Cancel edit"
            >
              <FontAwesomeIcon icon={faXmark} />
              <span className="visually-hidden"> Cancel</span>
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

/* Tag chip colors use app theme first, then Bootstrap as fallback
   If pp-theme.css defines:
     --pp-chip-bg, --pp-chip-fg, --pp-chip-border, --pp-chip-hover,
     --pp-chip-remove, --pp-chip-remove-hover
   they will be used automatically. Otherwise Bootstrap vars adapt to light/dark. */
